import type { BoardAssignment } from '@/hooks/use-staffing-board';

export type BoardTimeRange = 'all' | 'today' | 'tomorrow' | 'week';

export interface TimeRangeWindow {
  start: Date;
  end: Date;
}

function startOfDay(date: Date): Date {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
}

function endOfDay(date: Date): Date {
  const value = new Date(date);
  value.setHours(23, 59, 59, 999);
  return value;
}

function addDays(date: Date, days: number): Date {
  const value = new Date(date);
  value.setDate(value.getDate() + days);
  return value;
}

function startOfISOWeek(date: Date): Date {
  const value = startOfDay(date);
  const day = value.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  value.setDate(value.getDate() + diff);
  return value;
}

function endOfISOWeek(date: Date): Date {
  const value = startOfISOWeek(date);
  value.setDate(value.getDate() + 6);
  value.setHours(23, 59, 59, 999);
  return value;
}

export function getTimeRangeWindow(
  range: BoardTimeRange,
  now: Date,
): TimeRangeWindow | null {
  if (range === 'all') return null;
  if (range === 'today') {
    return {
      start: startOfDay(now),
      end: endOfDay(now),
    };
  }
  if (range === 'tomorrow') {
    const tomorrow = addDays(now, 1);
    return {
      start: startOfDay(tomorrow),
      end: endOfDay(tomorrow),
    };
  }
  return {
    start: startOfISOWeek(now),
    end: endOfISOWeek(now),
  };
}

function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function isProjectActiveInWindow(
  assignments: BoardAssignment[],
  window: TimeRangeWindow | null,
): boolean {
  if (!window) return true;
  if (assignments.length === 0) return false;

  return assignments.some((assignment) => {
    const assignmentStart = parseLocalDate(assignment.start_date);
    const assignmentEnd = assignment.end_date
      ? parseLocalDate(assignment.end_date)
      : null;

    return (
      assignmentStart <= window.end &&
      (assignmentEnd == null || assignmentEnd >= window.start)
    );
  });
}
